import React, { useMemo, useState } from 'react';

import {
  BackgroundType,
  FeaturedImageProps,
} from '@zola/zola-ui/src/components/PreAuthV3/FeaturedImage';
import Masthead from '@zola/zola-ui/src/components/PreAuthV3/Masthead';
import { useResponsiveDesign } from '@zola/zola-ui/src/contexts/ResponsiveDesignContext/ResponsiveDesignContext';

import cx from 'classnames';

import VendorsByCategory from '~/components/VendorsByCategory/VendorsByCategory';
import { useAccountIncentivizationContext } from '~/contexts/AccountIncentivizationContext';
import { useUserContext } from '~/contexts/UserContext';
import HERO_BACKGROUND from '~/pages/couples/explore/assets/hero-background.jpg';
import { BookedVendorLandingPagePromoModule } from '~/pages/couples/explore/components/BookedVendorPromoModule/BookedVendorPromoModule';
import { LANDING_PAGE_VALUES } from '~/pages/couples/explore/components/PreMarketplaceAccountContent/constants';
import EditLocationModal from '~/pages/couples/explore/components/SearchBar/EditLocationModal';
import { getAssetImageUrl } from '~/util/imageUtils';
import { hasMarketplaceAccount } from '~/util/userContextUtils';

import { OverlaySearchBar } from '../OverlaySearchBar/OverlaySearchBar';

import styles from './preMarketplaceAccountContent.module.less';

export const MastheadTitle = () => (
  <h1>
    <span>Wedding venues and vendors</span>
  </h1>
);

export const VendorsByCategoryContainer = ({
  className,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) => {
  return <section className={cx(styles.vendorsByCategory, className)} {...rest} />;
};

export const PreMarketplaceAccountContent = () => {
  const userContext = useUserContext();
  const { isMobile } = useResponsiveDesign();
  const [showLocationModal, setShowLocationModal] = useState(false);
  const mastheadImages: FeaturedImageProps = useMemo(() => {
    return {
      background: {
        type: BackgroundType.IMAGE,
        parameters: {
          featuredSrc: isMobile
            ? getAssetImageUrl(HERO_BACKGROUND, { width: 700, quality: 80 })
            : getAssetImageUrl(HERO_BACKGROUND, { width: 1000, quality: 80 }),
          alt: '',
        },
      },
    };
  }, [isMobile]);
  const { isBookedInquiryIncentiveAvailable } = useAccountIncentivizationContext();
  const showIncentive = useMemo(
    () => isBookedInquiryIncentiveAvailable(),
    [isBookedInquiryIncentiveAvailable]
  );
  if (hasMarketplaceAccount(userContext)) {
    return null;
  }
  return (
    <>
      <section>
        <Masthead
          productName="Wedding venues and vendors"
          title={<MastheadTitle />}
          valueProps={LANDING_PAGE_VALUES}
          btnText="Take our quiz"
          btnHref="/inspiration/onboard"
          images={mastheadImages}
          isPhotoOnRight
          hideImageNonDesktop
          className={cx({ [styles.noBorder]: showIncentive })}
        />
      </section>
      {showIncentive && (
        <BookedVendorLandingPagePromoModule showSearchBar={true} className={styles.hideMobile} />
      )}
      {!showIncentive && (
        <section className={cx(styles.withBottomBorder, { [styles.hideNotDesktop]: true })}>
          <OverlaySearchBar isClpTest={true} />
        </section>
      )}
      <VendorsByCategoryContainer className={styles.withBottomBorderNotMobile}>
        <VendorsByCategory isClpTest={true} showModal={() => setShowLocationModal(true)} />
        {showLocationModal && <EditLocationModal hideModalFn={() => setShowLocationModal(false)} />}
      </VendorsByCategoryContainer>
      {showIncentive && (
        <BookedVendorLandingPagePromoModule showSearchBar={false} className={styles.showMobile} />
      )}
    </>
  );
};
